import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { api, track, resolveImg } from "@/lib/api";
import { Lock, Download, Sparkles } from "lucide-react";
import GoldenParticles from "@/components/GoldenParticles";
import { toast } from "sonner";

export default function RevealPage() {
  const { lang, t } = useLang();
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [paying, setPaying] = useState(false);
  const [revealing, setRevealing] = useState(false);

  useEffect(() => {
    api.get(`/leads/${leadId}/preview`).then((r) => setPreview({ ...r.data, image_url: resolveImg(r.data.image_url) })).catch(() => {});
    track("preview_view", { lead_id: leadId });
  }, [leadId]);

  const startPayment = async () => {
    setPaying(true);
    track("payment_click", { lead_id: leadId });
    try {
      const { data: order } = await api.post("/orders/create", { lead_id: leadId });
      if (order.status === "already_paid" || order.mode === "free") {
        const { data } = await api.get(`/leads/${leadId}/preview`);
        handleUnlocked({ ...data, image_url: data.image_url });
        return;
      }
      if (order.mode === "razorpay") {
        // Razorpay checkout
        if (!window.Razorpay) {
          await new Promise((res, rej) => {
            const s = document.createElement("script");
            s.src = "https://checkout.razorpay.com/v1/checkout.js";
            s.onload = res; s.onerror = rej;
            document.body.appendChild(s);
          });
        }
        const rz = new window.Razorpay({
          key: order.key_id,
          amount: order.amount,
          currency: "INR",
          name: "SHREE RAGO",
          description: "Personalized Divine Image",
          image: `${window.location.origin}/logo-mark.png`,
          order_id: order.razorpay_order_id,
          prefill: order.prefill || {},
          notes: { lead_id: leadId },
          theme: { color: "#D6AA45" },
          modal: { ondismiss: () => setPaying(false) },
          handler: async (res) => {
            try {
              const { data } = await api.post("/orders/verify", {
                lead_id: leadId,
                razorpay_order_id: res.razorpay_order_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
              });
              handleUnlocked(data);
            } catch {
              toast.error(t("pay.failed"));
              setPaying(false);
            }
          },
        });
        rz.on("payment.failed", () => { toast.error(t("pay.failed")); setPaying(false); });
        rz.open();
        setPaying(false);
        return;
      }
      throw new Error("unexpected payment mode");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("pay.failed"));
      setPaying(false);
    }
  };

  const handleUnlocked = (data) => {
    setRevealing(true);
    setTimeout(() => {
      setPreview((p) => ({ ...p, image_url: resolveImg(data.image_url), payment_status: "paid" }));
      setRevealing(false);
      setPaying(false);
      track("image_reveal", { lead_id: leadId });
    }, 2200);
  };

  const downloadImage = async () => {
    if (!preview?.image_url) return;
    track("image_download", { lead_id: leadId });
    try {
      const resp = await fetch(preview.image_url, { mode: "cors" });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `shreerago-${preview.full_name || "image"}.jpg`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      window.open(preview.image_url, "_blank");
    }
  };

  if (!preview) {
    return <div className="min-h-screen bg-obsidian flex items-center justify-center text-muted-warm">{t("common.loading")}</div>;
  }

  const unlocked = preview.payment_status === "paid";

  return (
    <div className="min-h-screen bg-obsidian relative overflow-hidden">
      <GoldenParticles density={40} />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(214,170,69,0.1)_0%,transparent_60%)]" />

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-16 sm:py-24">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.35em] text-gold mb-3">
            {unlocked ? t("result.title") : t("locked.ready")}
          </p>
          <h1 className="font-serif-lux text-3xl sm:text-5xl text-gold-hi">
            {unlocked ? preview.deity : t("locked.title")}
          </h1>
          {unlocked && (
            <p className="mt-4 text-muted-warm text-sm">
              {t("result.selected_for")}: <span className="text-ivory">{preview.full_name}</span>
            </p>
          )}
        </div>

        {/* 9:16 preview card */}
        <div data-testid="locked-preview-card" className="relative mx-auto w-full max-w-[320px] aspect-[9/16] rounded-3xl overflow-hidden border border-gold-active shadow-[0_16px_64px_rgba(0,0,0,0.8)] gold-ring">
          {preview.image_url ? (
            <img
              src={preview.image_url}
              alt={preview.deity}
              className={`w-full h-full object-cover transition-all duration-1000 ${unlocked ? "filter-none scale-100" : "blur-2xl brightness-[0.35] scale-110"} ${revealing ? "opacity-0" : "opacity-100"}`}
            />
          ) : (
            <div className="w-full h-full bg-card-warm" />
          )}
          {/* dark veil */}
          {!unlocked && <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/70" />}
          {revealing && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70">
              <div className="text-gold-hi font-serif-lux text-lg animate-pulse">{t("result.revealing")}</div>
            </div>
          )}
          {!unlocked && !revealing && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="animate-gold-pulse w-20 h-20 rounded-full border-2 border-gold-active flex items-center justify-center bg-black/60">
                <Lock size={30} className="text-gold-hi" />
              </div>
            </div>
          )}
          {unlocked && (
            <div className="absolute bottom-4 left-4 right-4 text-center">
              <div className="inline-block px-4 py-1 rounded-full text-[10px] tracking-widest uppercase text-gold-hi border border-gold bg-black/60">
                {preview.theme}
              </div>
            </div>
          )}
        </div>

        {/* CTA area */}
        <div className="mt-10 text-center">
          {unlocked ? (
            <>
              <button data-testid="download-hd-wallpaper-btn" onClick={downloadImage} className="btn-gold text-sm inline-flex items-center gap-2">
                <Download size={16} /> {t("result.download")}
              </button>
              <div className="mt-6 text-xs text-muted-warm tracking-wider">{t("result.setwallpaper")}</div>
            </>
          ) : (
            <>
              <p className="text-muted-warm text-sm max-w-md mx-auto mb-6">{t("locked.body")}</p>
              <div className="glass-panel rounded-2xl p-6 max-w-md mx-auto">
                <div className="text-xs uppercase tracking-widest text-muted-warm">{t("pay.item")}</div>
                {preview.pricing?.coupon ? (
                  <div className="mt-2" data-testid="reveal-pricing-discounted">
                    <span className="font-serif-lux text-2xl text-muted-warm line-through mr-3">₹{preview.pricing.list_paise / 100}</span>
                    <span className="font-serif-lux text-4xl gold-shimmer">₹{preview.pricing.final_paise / 100}</span>
                    <div className="text-[11px] text-gold mt-1 tracking-widest" data-testid="reveal-coupon-code">{preview.pricing.coupon.code} · −₹{preview.pricing.discount_paise / 100}</div>
                  </div>
                ) : (
                  <div className="mt-2 flex items-end justify-center gap-3" data-testid="reveal-pricing">
                    <span className="font-serif-lux text-2xl text-muted-warm line-through mb-1">₹199</span>
                    <span className="font-serif-lux text-4xl gold-shimmer">₹99</span>
                  </div>
                )}
                <div className="text-[11px] text-muted-warm mt-1">{t("pay.no_hidden")}</div>
                <RevealCoupon leadId={leadId} pricing={preview.pricing} onChange={(pricing) => setPreview((p) => ({ ...p, pricing }))} />
                <button data-testid="unlock-pay-199-btn" onClick={startPayment} disabled={paying} className="btn-gold w-full text-sm mt-6 disabled:opacity-60">
                  {paying ? "…" : preview.pricing?.coupon ? (preview.pricing.final_paise === 0 ? t("locked.cta.free") : t("locked.cta").replace("99", String(preview.pricing.final_paise / 100))) : t("locked.cta")}
                </button>
                <div className="mt-4 text-[11px] text-muted-warm tracking-wider flex items-center justify-center gap-1">
                  <Sparkles size={12} /> {t("locked.trust")}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mt-14 text-center space-y-3">
          <button onClick={() => navigate(`/${lang}`)} className="text-xs uppercase tracking-[0.25em] text-muted-warm hover:text-gold-hi">
            {t("result.back")}
          </button>
          <div>
            <button onClick={() => navigate(`/${lang}/access`)} className="text-[11px] uppercase tracking-[0.2em] text-gold hover:text-gold-hi" data-testid="reveal-access-again-link">
              {t("access.link")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RevealCoupon({ leadId, pricing, onChange }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: code.trim().toUpperCase(), lead_id: leadId });
      onChange(data);
      toast.success(t("coupon.applied").replace("{code}", data.coupon.code));
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("coupon.invalid"));
    } finally { setBusy(false); }
  };
  const remove = async () => {
    const { data } = await api.delete(`/leads/${leadId}/coupon`);
    onChange(data);
    setCode("");
  };
  if (pricing?.coupon) {
    return <button onClick={remove} className="mt-3 text-[11px] text-muted-warm hover:text-red-300 underline" data-testid="reveal-coupon-remove">{t("coupon.remove")}</button>;
  }
  if (!open) {
    return <button onClick={() => setOpen(true)} className="mt-3 text-[11px] uppercase tracking-[0.2em] text-gold hover:text-gold-hi" data-testid="reveal-coupon-toggle">{t("coupon.have")}</button>;
  }
  return (
    <div className="flex gap-2 mt-3" data-testid="reveal-coupon-form">
      <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => { if (e.key === "Enter") apply(); }} placeholder={t("coupon.ph")} className="input-warm flex-1 text-sm uppercase tracking-widest py-2" data-testid="reveal-coupon-input" />
      <button onClick={apply} disabled={busy || !code} className="btn-outline-gold text-xs px-4 disabled:opacity-50" data-testid="reveal-coupon-apply">{busy ? "…" : t("coupon.apply")}</button>
    </div>
  );
}
